
                    // src/pages/Landingpage.jsx - Complete Enhanced Version
// Use optimized public images from /imgs when available (generated webp variants)

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  Paper,
  Avatar,
  Chip,
  Fade,
  Slide,
  Zoom,
  Grid,
  useTheme,
  useMediaQuery,
  Skeleton,
  IconButton,
  Alert,
} from '@mui/material';
import {
  FindInPage,
  QuestionAnswer,
  Event,
  Security,
  Group,
  PhoneAndroid,
  PlayArrow,
  ArrowForward,
  AutoAwesome,
  Psychology,
  TrendingUp,
  FormatQuote,
  ChevronLeft,
  ChevronRight,
  Verified,
  School,
  CheckCircle,
  Star,
  Rocket,
} from '@mui/icons-material';

// Import reusable components
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import PeerCampusDemoModal from '../../components/PeerCampusDemoModal';
import ScrollToTopButton from '../../components/ScrollToTopButton';

const DEMO_PARTNER_FORM_URL = 'https://docs.google.com/forms/d/e/1FAIpQLScvBbbyZIy9H-uIImuWbRrYSsjjylynEF-qPIuALQqHCo6Egg/viewform?usp=publish-editor';
const DEMO_VIDEO_URL = 'https://www.youtube.com/embed/11aWT8WM4oM?si=ZFRbzDCP5ejialk5';

/**
 * Enhanced TestimonialCard with better accessibility and visual design
 */
const TestimonialCard = ({ testimonial, isActive = false, isVisible = false }) => {
  const designTokens = {
    colors: {
      primary: '#0D1C4F',
      accent: '#016f57',
      white: '#FFFFFF',
      lightBlue: '#EFF6FF',
      success: '#019a78',
      text: '#1F2937',
      textLight: '#6B7280',
    },
    borderRadius: {
      xl: '20px',
    },
    shadows: {
      sm: '0 2px 8px rgba(13, 28, 79, 0.1)',
      md: '0 4px 16px rgba(13, 28, 79, 0.15)',
      lg: '0 8px 32px rgba(13, 28, 79, 0.2)',
      xl: '0 16px 48px rgba(13, 28, 79, 0.25)',
    },
  };

  return (
    <Paper
      role="article"
      aria-label={`Testimonial by ${testimonial.author}`}
      sx={{
        p: { xs: 3, md: 4 },
        borderRadius: designTokens.borderRadius.xl,
        background: designTokens.colors.white,
        boxShadow: isActive ? designTokens.shadows.xl : designTokens.shadows.md,
        height: '100%',
        minHeight: { xs: 340, md: 380 },
        border: `2px solid ${isActive ? designTokens.colors.success : isVisible ? designTokens.colors.accent : designTokens.colors.lightBlue}`,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        lineHeight: 1.6,
        transform: isActive ? 'translateY(-12px) scale(1.05)' : isVisible ? 'translateY(-4px) scale(1.02)' : 'translateY(0) scale(1)',
        opacity: isVisible ? 1 : 0.7,
        '&:hover': {
          transform: isActive ? 'translateY(-16px) scale(1.06)' : 'translateY(-8px) scale(1.03)',
          boxShadow: designTokens.shadows.xl,
          borderColor: designTokens.colors.success,
          opacity: 1,
        },
        transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
        zIndex: isActive ? 3 : isVisible ? 2 : 1,
      }}
    >
      <FormatQuote
        sx={{
          fontSize: { xs: 28, md: 32 },
          color: designTokens.colors.primary,
          opacity: 0.15,
          position: 'absolute',
          top: { xs: 12, md: 16 },
          left: { xs: 16, md: 20 },
        }}
        aria-hidden="true"
      />

      {/* Star Rating */}
      <Box sx={{ display: 'flex', gap: 0.5, mb: 2, mt: 2, justifyContent: 'flex-end' }}>
        {[...Array(5)].map((_, i) => (
          <Star key={i} sx={{ fontSize: 16, color: '#016f57' }} />
        ))}
      </Box>

      {/* Quote text with flexible height */}
      <Typography
        component="blockquote"
        sx={{
          color: designTokens.colors.textLight,
          mb: 4,
          lineHeight: 1.7,
          fontStyle: 'italic',
          flex: 1,
          fontSize: { xs: '0.95rem', md: '1rem' },
          maxWidth: '75ch',
          fontWeight: 400,
          minHeight: { xs: '4rem', md: '5rem' },
        }}
      >
        "{testimonial.text}"
      </Typography>

      {/* Author info at bottom */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 'auto' }}>
        <Avatar
          sx={{
            backgroundColor: designTokens.colors.primary,
            fontWeight: 700,
            width: { xs: 44, md: 48 },
            height: { xs: 44, md: 48 },
            fontSize: { xs: '1rem', md: '1.1rem' },
          }}
          alt={`${testimonial.author} avatar`}
        >
          {testimonial.avatar}
        </Avatar>

        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: designTokens.colors.text,
                fontSize: { xs: '0.95rem', md: '1rem' },
              }}
            >
              {testimonial.author}
            </Typography>
            {testimonial.verified && (
              <Verified
                sx={{
                  fontSize: 18,
                  color: designTokens.colors.success,
                }}
                aria-label="Verified student"
              />
            )}
          </Box>

          <Typography 
            variant="body2" 
            sx={{ 
              color: designTokens.colors.textLight, 
              fontSize: { xs: '0.85rem', md: '0.9rem' },
              fontWeight: 500,
              mb: 0.5,
            }}
          >
            {testimonial.role}
          </Typography>

          <Typography 
            variant="body2" 
            sx={{ 
              color: designTokens.colors.primary, 
              fontWeight: 600, 
              fontSize: { xs: '0.8rem', md: '0.85rem' } 
            }}
          >
            {testimonial.college}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

/**
 * Enhanced FeatureCard with better visual hierarchy
 */
const FeatureCard = ({ feature, index, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  const designTokens = {
    colors: {
      primary: '#0D1C4F',
      white: '#FFFFFF',
      lightBlue: '#EFF6FF',
    },
    borderRadius: { xl: '20px' },
    shadows: {
      md: '0 4px 16px rgba(13, 28, 79, 0.15)',
      xl: '0 16px 48px rgba(13, 28, 79, 0.25)',
    },
  };

  return (
    <Card
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onClick && onClick(feature)}
      sx={{
        /* Reduce padding and minHeight to match previous, denser card layout */
        p: { xs: 2, md: 3 },
        minHeight: { xs: 220, md: 260 },
        borderRadius: designTokens.borderRadius.xl,
        border: `2px solid ${isHovered ? feature.color : designTokens.colors.lightBlue}`,
        background: designTokens.colors.white,
        // Lighter default shadow so cards read smaller
        boxShadow: isHovered ? designTokens.shadows.xl : '0 4px 12px rgba(13,28,79,0.06)',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isHovered ? 'translateY(-12px) scale(1.02)' : 'translateY(0) scale(1)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg, ${feature.color}, ${feature.color}aa)`,
          transform: isHovered ? 'scaleX(1)' : 'scaleX(0)',
          transformOrigin: 'left',
          transition: 'transform 0.4s ease',
        },
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick && onClick(feature);
        }
      }}
      aria-label={`Learn more about ${feature.title}`}
    >
      <Box
        sx={{
          /* Smaller icon container to reduce overall card height */
          width: { xs: 44, md: 48 },
          height: { xs: 44, md: 48 },
          borderRadius: '12px',
          background: `linear-gradient(135deg, ${feature.color}20, ${feature.color}10)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: '12px',
            background: `linear-gradient(135deg, ${feature.color}40, transparent)`,
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 0.3s ease',
          },
        }}
      >
        {React.cloneElement(feature.icon, {
          sx: {
            fontSize: { xs: 28, md: 32 },
            color: feature.color,
            position: 'relative',
            zIndex: 1,
          }
        })}
      </Box>

      <Typography 
        variant="h5" 
        sx={{ 
          fontWeight: 700, 
          mb: 2,
          color: designTokens.colors.primary,
          /* Slightly smaller heading to reduce visual bulk */
          fontSize: { xs: '0.95rem', md: '1rem' },
          lineHeight: 1.2,
        }}
      >
        {feature.title}
      </Typography>

      <Typography 
        variant="body1" 
        sx={{ 
          color: designTokens.colors.text, 
          lineHeight: 1.5,
          fontSize: { xs: '0.85rem', md: '0.9rem' },
          mb: 2,
          flex: 1,
        }}
      >
        {feature.description}
      </Typography>

      {/* Outcome chip at bottom */}
      <Box sx={{ mt: 'auto' }}> {/* Remove padding-top */}
        <Chip
            label={feature.outcome}
            sx={{
              backgroundColor: `${feature.color}15`,
              color: feature.color,
              fontWeight: 600,
              fontSize: { xs: '0.65rem', md: '0.7rem' },
              height: 20,
              borderRadius: '10px',
              '& .MuiChip-label': {
                px: 1,
              },
            }}
            icon={<CheckCircle sx={{ fontSize: '14px !important' }} />}
          />
      </Box>
    </Card>
  );
};

/**
 * Enhanced Loading Skeleton Component
 */
const LoadingSkeleton = () => (
  <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>
    <Grid container alignItems="center" spacing={4}>
      <Grid item xs={12} md={6}>
        <Skeleton variant="text" width="80%" height={60} />
        <Skeleton variant="text" width="60%" height={40} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="90%" height={30} />
        <Skeleton variant="text" width="70%" height={30} sx={{ mb: 4 }} />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Skeleton variant="rectangular" width={150} height={48} />
          <Skeleton variant="rectangular" width={150} height={48} />
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <Skeleton variant="rectangular" width="100%" height={400} />
      </Grid>
    </Grid>
  </Container>
);

const PeerCampusLanding = () => {
  const [loaded, setLoaded] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [animatedStats, setAnimatedStats] = useState({});
  const [imageLoaded, setImageLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [activeCardIndex, setActiveCardIndex] = useState(0);

  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const isXlUp = useMediaQuery(theme.breakpoints.up('xl'));

  const cardsPerView = useMemo(() => {
    if (isXlUp) return 4;
    if (isLgUp) return 3;
    if (isMdUp) return 2;
    if (isSmUp) return 2;
    return 1;
  }, [isXlUp, isLgUp, isMdUp, isSmUp]);

  const currentTestimonialRef = useRef(currentTestimonial);
  const activeCardIndexRef = useRef(activeCardIndex);

  useEffect(() => {
    currentTestimonialRef.current = currentTestimonial;
  }, [currentTestimonial]);

  useEffect(() => {
    activeCardIndexRef.current = activeCardIndex;
  }, [activeCardIndex]);

  // Enhanced design system with better color palette
  const designTokens = useMemo(() => ({
    colors: {
      primary: '#0D1C4F',
      secondary: '#1E3A8A',
      accent: '#016f57',
      light: '#F8FAFC',
      lightBlue: '#EFF6FF',
      text: '#1F2937',
      textLight: '#6B7280',
      white: '#FFFFFF',
      gradient: 'linear-gradient(135deg, #0D1C4F 0%, #1E3A8A 100%)',
      gradientLight: 'linear-gradient(135deg, #EFF6FF 0%, #F8FAFC 100%)',
      gradientAccent: 'linear-gradient(135deg, #016f57 0%, #019a78 100%)',
      success: '#019a78',
      warning: '#016f57',
      purple: '#016f57',
      error: '#EF4444',
    },
    spacing: {
      xs: 8, sm: 16, md: 24, lg: 32, xl: 48, xxl: 64,
    },
    borderRadius: {
      sm: '8px', md: '12px', lg: '16px', xl: '20px', xxl: '24px',
    },
    shadows: {
      sm: '0 2px 8px rgba(13, 28, 79, 0.1)',
      md: '0 4px 16px rgba(13, 28, 79, 0.15)',
      lg: '0 8px 32px rgba(13, 28, 79, 0.2)',
      xl: '0 16px 48px rgba(13, 28, 79, 0.25)',
    },
    typography: {
      h1: {
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
        fontWeight: 800,
        fontSize: { xs: '2.5rem', sm: '3.2rem', md: '3.8rem', lg: '4.2rem' },
        lineHeight: { xs: 1.15, md: 1.1 },
        letterSpacing: '-0.025em',
      },
      h2: {
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
        fontWeight: 700,
        fontSize: { xs: '2rem', md: '2.8rem' },
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
      },
      body: {
        fontSize: { xs: '1rem', md: '1.125rem' },
        lineHeight: 1.7,
        fontWeight: 400,
      },
    },
  }), []);

  // Enhanced features data with better outcomes
  const features = useMemo(() => [
    {
      icon: <FindInPage />,
      title: 'AI Lost & Found System',
      description: 'Smart image recognition technology finds your lost items with advanced AI matching',
      outcome: '95% success rate in <2 hours',
      color: designTokens.colors.warning,
      link: '/features/lost-found',
    },
    {
      icon: <Group />,
      title: 'Student Skill Marketplace',
      description: 'Connect with peers, share expertise, and collaborate on projects seamlessly',
      outcome: 'Match with study partners in <5 min',
      color: designTokens.colors.accent,
      link: '/features/marketplace',
    },
    {
      icon: <QuestionAnswer />,
      title: 'GPT-Powered Doubt Board',
      description: 'Get instant answers from AI and community experts available 24/7',
      outcome: '90% questions resolved instantly',
      color: designTokens.colors.purple,
      link: '/features/doubt-board',
    },
    {
      icon: <Event />,
      title: 'Smart Event Hub',
      description: 'Never miss campus events with intelligent notifications and RSVP tracking',
      outcome: 'Track 100+ campus events monthly',
      color: designTokens.colors.purple,
      link: '/features/events',
    },
    {
      icon: <Security />,
      title: 'Verified College Network',
      description: 'Secure, college-verified community you can trust with advanced privacy controls',
      outcome: 'Connect safely with verified students',
      color: designTokens.colors.success,
      link: '/features/security',
    },
    {
      icon: <PhoneAndroid />,
      title: 'Mobile-First Experience',
      description: 'Seamless cross-platform experience with offline capabilities and sync',
      outcome: 'Works offline, syncs everywhere',
      color: designTokens.colors.warning,
      link: '/features/mobile',
    },
  ], [designTokens.colors]);

  const demoFeatures = useMemo(() => [
    'Find lost items in minutes using AI',
    'Connect with the right study partners',
    'Get instant academic help with AI',
    'Manage campus events effortlessly',
  ], []);

  const stats = useMemo(() => [
    {
      number: animatedStats.students || 0,
      suffix: '+',
      label: 'Active Students',
      icon: <Group />,
      color: designTokens.colors.accent,
    },
    {
      number: animatedStats.doubts || 0,
      suffix: '+',
      label: 'Doubts Resolved',
      icon: <Psychology />,
      color: designTokens.colors.purple,
    },
    {
      number: animatedStats.items || 0,
      suffix: '+',
      label: 'Items Recovered',
      icon: <TrendingUp />,
      color: designTokens.colors.warning,
    },
  ], [animatedStats, designTokens.colors]);

  const testimonials = useMemo(() => [
    {
      text: "PeerCampus completely transformed how I connect with my classmates. Found my lost laptop in just 2 hours using their incredible AI image search technology!",
      author: 'Sarah M.',
      role: 'Computer Science Student',
      college: 'MIT',
      avatar: 'S',
      verified: true,
    },
    {
      text: 'The AI doubt board is absolutely incredible. Got my complex calculus questions answered at 2 AM when no one else was awake - saved my exam prep!',
      author: 'Raj P.',
      role: 'Engineering Student',
      college: 'Stanford University',
      avatar: 'R',
      verified: true,
    },
    {
      text: "The skill marketplace is a game changer! Found the perfect study partner for my final project within minutes. We're now collaborating on multiple ventures!",
      author: 'Emma L.',
      role: 'Business Student',
      college: 'Harvard Business School',
      avatar: 'E',
      verified: true,
    },
    {
      text: 'Smart event notifications helped me discover amazing networking opportunities and workshops I would have completely missed otherwise. Absolutely love it!',
      author: 'Alex K.',
      role: 'Psychology Major',
      college: 'UC Berkeley',
      avatar: 'A',
      verified: true,
    },
  ], []);

  // Enhanced animation function with error handling
  const animateValue = useCallback((start, end, duration, key) => {
    let startTimestamp = null;
    const step = (timestamp) => {
      try {
        if (!startTimestamp) startTimestamp = timestamp;
        const progress = Math.min((timestamp - startTimestamp) / duration, 1);
        const current = Math.floor(progress * (end - start) + start);
        setAnimatedStats((prev) => ({ ...prev, [key]: current }));
        if (progress < 1) window.requestAnimationFrame(step);
      } catch (err) {
        console.error('Animation error:', err);
        setError('Animation failed to load');
      }
    };
    window.requestAnimationFrame(step);
  }, []);

  // Enhanced lifecycle effects
  useEffect(() => {
    try {
      setLoaded(true);

      // Staggered stat animations
      const timeout = setTimeout(() => {
        animateValue(0, 1200, 2000, 'students');
        setTimeout(() => animateValue(0, 3000, 2200, 'doubts'), 200);
        setTimeout(() => animateValue(0, 250, 1800, 'items'), 400);
      }, 1000);

      return () => {
        clearTimeout(timeout);
      };
    } catch (err) {
      console.error('Component initialization error:', err);
      setError('Failed to initialize page');
    }
  }, [animateValue]);

  const testimonialPages = useMemo(() => {
    const chunkSize = Math.max(1, cardsPerView);
    const chunks = [];
    for (let i = 0; i < testimonials.length; i += chunkSize) {
      chunks.push(testimonials.slice(i, i + chunkSize));
    }
    return chunks;
  }, [cardsPerView, testimonials]);

  const totalTestimonialPages = testimonialPages.length;
  const safeTotalPages = Math.max(1, totalTestimonialPages);

  useEffect(() => {
    if (!testimonialPages.length) {
      currentTestimonialRef.current = 0;
      activeCardIndexRef.current = 0;
      setCurrentTestimonial(0);
      setActiveCardIndex(0);
      return;
    }

    setCurrentTestimonial((prevPage) => {
      const maxPage = testimonialPages.length - 1;
      const nextPage = Math.min(prevPage, maxPage);
      currentTestimonialRef.current = nextPage;

      const cardsOnPage = testimonialPages[nextPage]?.length || 1;
      const nextActiveIndex = Math.min(activeCardIndexRef.current, Math.max(0, cardsOnPage - 1));
      activeCardIndexRef.current = nextActiveIndex;
      setActiveCardIndex(nextActiveIndex);

      return nextPage;
    });
  }, [testimonialPages]);

  // Auto-rotate testimonials with individual card progression
  useEffect(() => {
    if (!testimonialPages.length) return undefined;

    const interval = setInterval(() => {
      const pageIndex = currentTestimonialRef.current;
      const cardsOnPage = testimonialPages[pageIndex]?.length || 0;

      if (cardsOnPage <= 1) {
        const nextPage = (pageIndex + 1) % testimonialPages.length;
        currentTestimonialRef.current = nextPage;
        activeCardIndexRef.current = 0;
        setCurrentTestimonial(nextPage);
        setActiveCardIndex(0);
        return;
      }

      const nextActive = activeCardIndexRef.current + 1;
      if (nextActive < cardsOnPage) {
        activeCardIndexRef.current = nextActive;
        setActiveCardIndex(nextActive);
        return;
      }

      const nextPage = (pageIndex + 1) % testimonialPages.length;
      currentTestimonialRef.current = nextPage;
      activeCardIndexRef.current = 0;
      setCurrentTestimonial(nextPage);
      setActiveCardIndex(0);
    }, 3200);

    return () => clearInterval(interval);
  }, [testimonialPages]);

  // Enhanced event handlers
  const handleFeatureClick = useCallback((feature) => {
    // console.log(`Navigate to ${feature.link}`);
    // Add actual navigation logic here
  }, []);

  const handleVideoDemo = useCallback(() => setVideoModalOpen(true), []);

  const handleTestimonialNav = useCallback((direction) => {
    if (totalTestimonialPages <= 0) {
      return;
    }

    if (direction === 'next') {
      setCurrentTestimonial((prev) => {
        const nextPage = totalTestimonialPages <= 1 ? 0 : (prev + 1) % totalTestimonialPages;
        currentTestimonialRef.current = nextPage;
        activeCardIndexRef.current = 0;
        return nextPage;
      });
      setActiveCardIndex(0);
    } else {
      setCurrentTestimonial((prev) => {
        if (totalTestimonialPages <= 1) {
          currentTestimonialRef.current = 0;
          activeCardIndexRef.current = 0;
          return 0;
        }
        const nextPage = (prev - 1 + totalTestimonialPages) % totalTestimonialPages;
        currentTestimonialRef.current = nextPage;
        activeCardIndexRef.current = 0;
        return nextPage;
      });
      setActiveCardIndex(0);
    }
  }, [totalTestimonialPages]);

  // Error boundary fallback
  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 4 }}>
          <Typography variant="h6">Something went wrong</Typography>
          <Typography variant="body2">{error}</Typography>
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
        >
          Reload Page
        </Button>
      </Container>
    );
  }

  // Loading state
  if (!loaded) {
    return (
      <Box sx={{ minHeight: '100vh', background: designTokens.colors.gradientLight }}>
        <Navbar />
        <LoadingSkeleton />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: designTokens.colors.gradientLight, position: 'relative', overflow: 'hidden' }}>
      {/* Enhanced decorative elements */}
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          top: -100,
          right: -100,
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${designTokens.colors.primary}08 0%, transparent 70%)`,
          zIndex: 0,
          display: { xs: 'none', md: 'block' },
          animation: 'float 6s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
            '50%': { transform: 'translateY(-20px) rotate(180deg)' },
          },
        }}
      />

  <a href="#main-content" className="skip-link">Skip to content</a>
  <Navbar />

      {/* Enhanced Hero Section */}
      <Container 
        id="main-content" 
        maxWidth={false} 
        sx={{
          pt: { xs: 5.5, sm: 6.5, md: 7, lg: 8 },
          pb: { xs: 6, sm: 6.5, md: 8, lg: 10 },
          position: 'relative',
          zIndex: 1,
          maxWidth: { xs: 'var(--container-max-width)', xl: '1500px' },
          mx: 'auto',
          px: 'var(--container-padding-x)',
          width: '100%'
        }}
      >
        <Box
          component="div"
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'minmax(0, 1.02fr) minmax(0, 0.98fr)',
              lg: 'minmax(0, 0.95fr) minmax(0, 1.05fr)',
              xl: 'minmax(0, 0.9fr) minmax(0, 1.1fr)',
            },
            alignItems: { xs: 'center', md: 'start' },
            justifyItems: { xs: 'center', md: 'stretch' },
            gap: { xs: 4, sm: 5, md: 4.5, lg: 6 },
            width: '100%',
          }}
        >
          <Box sx={{ pr: { md: 2, lg: 4 }, width: '100%' }}>
            <Slide direction="right" in={loaded} timeout={1000}>
              <Box sx={{ width: '100%', textAlign: { xs: 'center', md: 'left' } }}>
                <Typography
                  variant="h1"
                  sx={{
                    ...designTokens.typography.h1,
                    fontSize: {
                      xs: 'clamp(2.45rem, 11vw, 3.2rem)',
                      sm: 'clamp(2.9rem, 8.6vw, 4rem)',
                      md: 'clamp(3.85rem, 5.9vw, 5.05rem)',
                      lg: 'clamp(4.75rem, 5.3vw, 6.25rem)',
                      xl: 'clamp(5.35rem, 4.9vw, 6.9rem)',
                    },
                    lineHeight: { xs: 1.08, sm: 1.06, md: 1.04, lg: 1.07 },
                    color: designTokens.colors.text,
                    mb: { xs: 2.5, md: 2.2, lg: 3 },
                    maxWidth: { xs: '14ch', sm: '15ch', md: '13ch', lg: '24ch', xl: '26ch' },
                    mx: { xs: 'auto', md: 0 },
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'inline', lg: 'block' }, whiteSpace: { lg: 'nowrap' } }}>
                    Unite, Learn &
                  </Box>{' '}
                  <Box component="span" sx={{ display: { xs: 'inline', lg: 'block' }, whiteSpace: { lg: 'nowrap' } }}>
                    Grow with{' '}
                  </Box>
                  <Box 
                    component="span" 
                    sx={{
                      background: designTokens.colors.gradient,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      position: 'relative',
                      display: 'inline-block',
                      whiteSpace: { lg: 'nowrap' },
                    }}
                  >
                    AI Power
                    <AutoAwesome 
                      sx={{
                        position: 'absolute',
                        top: { xs: -8, md: -12 },
                        right: { xs: -12, md: -18 },
                        fontSize: { xs: 24, md: 34, lg: 40 },
                        color: designTokens.colors.accent,
                        filter: 'drop-shadow(0 2px 4px rgba(1, 111, 87, 0.3))',
                        animation: 'sparkle 2s ease-in-out infinite',
                        '@keyframes sparkle': {
                          '0%, 100%': { opacity: 1, transform: 'scale(1) rotate(0deg)' },
                          '50%': { opacity: 0.7, transform: 'scale(1.1) rotate(180deg)' }
                        },
                      }} 
                    />
                  </Box>
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    ...designTokens.typography.body,
                    color: designTokens.colors.text,
                    mb: { xs: 3, md: 2.5, lg: 3 },
                    maxWidth: { xs: '100%', sm: 520, md: 480, lg: 520, xl: 560 },
                    mx: { xs: 'auto', md: 0 },
                    fontSize: { xs: '1rem', sm: '1.04rem', md: '1.26rem', lg: '1.36rem', xl: '1.44rem' },
                    lineHeight: { xs: 1.65, md: 1.62, lg: 1.7 },
                    fontWeight: 500,
                  }}
                >
                  Your complete AI-powered campus ecosystem for connecting students,
                  sharing skills, resolving doubts, and staying in the loop.{' '}
                  <Box 
                    component="span" 
                    sx={{
                      color: designTokens.colors.primary,
                      fontWeight: 700,
                      background: `linear-gradient(135deg, ${designTokens.colors.primary} 0%, ${designTokens.colors.accent} 100%)`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    Experience the future of college life.
                  </Box>
                </Typography>

                  <Box
  sx={{
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    gap: 2,
    alignItems: 'stretch',
    mb: { xs: 3, md: 3.2, lg: 4 },
    width: '100%',
    maxWidth: { xs: '100%', sm: 520, md: 430, lg: 520 },
    mx: { xs: 'auto', md: 0 },
  }}
>
  {/* PRIMARY BUTTON */}
  <Button
    variant="contained"
    endIcon={<ArrowForward />}
    href="/login"
    aria-label="Log in to PeerCampus"
    sx={{
      flex: 1,
      minWidth: 0,
      width: '100%',
      height: { xs: 52, sm: 56, md: 68 },
      borderRadius: '14px',
      textTransform: 'none',
      fontSize: { xs: '0.98rem', sm: '1rem', md: '1.12rem' },
      fontWeight: 600,
      whiteSpace: 'nowrap',
      background: designTokens.colors.gradient,
      boxShadow: '0 10px 25px rgba(0,0,0,0.12)',
      transition: 'all 0.25s ease',
      display: 'flex',
      justifyContent: 'center',
      gap: 1,
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 15px 35px ${designTokens.colors.primary}40`,
      },
    }}
  >
    Get Started Now
  </Button>

  {/* SECONDARY BUTTON */}
  <Button
    variant="outlined"
    startIcon={<PlayArrow />}
    onClick={handleVideoDemo}
    aria-label="Watch PeerCampus demo"
    sx={{
      flex: 1,
      minWidth: 0,
      width: '100%',
      height: { xs: 52, sm: 56, md: 68 },
      borderRadius: '14px',
      textTransform: 'none',
      fontSize: { xs: '0.98rem', sm: '1rem', md: '1.12rem' },
      fontWeight: 600,
      whiteSpace: 'nowrap',
      borderWidth: 2,
      borderColor: designTokens.colors.primary,
      color: designTokens.colors.primary,
      backgroundColor: 'white',
      transition: 'all 0.25s ease',
      display: 'flex',
      justifyContent: 'center',
      gap: 1,
      '&:hover': {
        backgroundColor: `${designTokens.colors.primary}08`,
        borderWidth: 2,
        transform: 'translateY(-4px)',
        boxShadow: `0 12px 30px ${designTokens.colors.primary}25`,
      },
    }}
  >
    Watch Demo
  </Button>
</Box>


                {/* Trust indicators */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  gap: 2, 
                  flexWrap: 'wrap',
                  opacity: 0.8,
                  maxWidth: { xs: 520, md: 'none' },
                  mx: { xs: 'auto', md: 0 },
                }}>
                  <Typography variant="body2" sx={{ color: designTokens.colors.textLight, fontWeight: 500 }}>
                    Trusted by 20+ universities
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} sx={{ fontSize: 16, color: designTokens.colors.warning }} />
                    ))}
                    <Typography variant="body2" sx={{ color: designTokens.colors.textLight, ml: 0.5 }}>
                      4.9/5 rating
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Slide>
          </Box>

          {/* Enhanced Hero Image with loading state */}
          <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' }, alignItems: { xs: 'center', md: 'flex-start' }, alignSelf: { xs: 'center', md: 'start' }, width: '100%', pl: { md: 1, lg: 5, xl: 6 }, pt: { xs: 2, sm: 2.5, md: 0 }, position: 'relative' }}>
            <Zoom in={loaded && imageLoaded} timeout={1200}>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', width: '100%', maxWidth: { xs: 380, sm: 500, md: 560, lg: 760, xl: 1020 }, mx: { xs: 'auto', md: 0 } }}>
                {!imageLoaded && (
                  <Skeleton 
                    variant="rectangular" 
                    width="100%" 
                    height={320}
                    sx={{ borderRadius: '24px' }}
                  />
                )}
                <Box component="picture">
                  <source
                    type="image/webp"
                    srcSet="/imgs/landing-hero-480.webp 480w, /imgs/landing-hero-768.webp 768w, /imgs/landing-hero-1024.webp 1024w"
                    sizes="(max-width: 899px) 92vw, (max-width: 1199px) 70vw, (max-width: 1535px) 52vw, 46vw"
                  />
                  <Box
                    component="img"
                    src="/imgs/landing-hero.webp"
                    alt="Students collaborating on PeerCampus platform - diverse group working together"
                    onLoad={() => setImageLoaded(true)}
                    onError={() => setError('Failed to load hero image')}
                    sx={{
                      width: '100%',
                      height: 'auto',
                      maxWidth: { xs: 360, sm: 500, md: 560, lg: 760, xl: 1020 },
                      maxHeight: { xs: 340, sm: 440, md: 460, lg: 650, xl: 820 },
                      objectFit: 'contain',
                      borderRadius: '24px',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      filter: 'drop-shadow(0 24px 48px rgba(13, 28, 79, 0.18))',
                      display: imageLoaded ? 'block' : 'none',
                      '&:hover': {
                        transform: 'scale(1.02) translateY(-4px)',
                        filter: 'drop-shadow(0 30px 60px rgba(13, 28, 79, 0.22))',
                      }
                    }}
                  />
                </Box>
                {/* Decorative element behind image */}
                <Box
                  aria-hidden="true"
                  sx={{
                    position: 'absolute',
                    top: '10%',
                    right: '10%',
                    width: '80%',
                    height: '80%',
                    borderRadius: '24px',
                    background: designTokens.colors.gradientAccent,
                    opacity: 0.1,
                    zIndex: -1,
                    transform: 'rotate(6deg)',
                  }}
                />
              </Box>
            </Zoom>
          </Box>
        </Box>
      </Container>

      {/* Enhanced Features Section */}
      <Box 
        component="section" 
        aria-labelledby="features-heading"
        sx={{ 
          py: { xs: 6.5, md: 8 }, 
          background: designTokens.colors.light,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: `linear-gradient(90deg, transparent, ${designTokens.colors.primary}20, transparent)`,
          }
        }}
      >
        <Container maxWidth="lg">
    <Box sx={{ textAlign: 'center', mb: { xs: 4.5, md: 5.5 } }}>
      <Chip
        label="Platform Features"
        sx={{
          backgroundColor: `${designTokens.colors.accent}15`,
          color: designTokens.colors.accent,
          fontWeight: 600,
          mb: 2.5,
          fontSize: '0.9rem',
          height: 32,
        }}
        icon={<Rocket sx={{ fontSize: '18px !important' }} />}
      />
      
      <Typography 
        id="features-heading"
        variant="h2" 
        sx={{ 
          ...designTokens.typography.h2,
          color: designTokens.colors.text, 
          mb: 3,
        }}
      >
        Why Choose PeerCampus?
      </Typography>
      
      <Typography
        variant="h6"
        sx={{
          color: designTokens.colors.textLight,
          maxWidth: 620,
          mx: 'auto',
          lineHeight: 1.6,
          fontSize: { xs: '1.1rem', md: '1.25rem' },
          fontWeight: 500,
        }}
      >
        Revolutionary AI-powered features that make campus life smarter, 
        more connected, and incredibly efficient
      </Typography>
    </Box>

    {/* Fixed Responsive Grid Layout */}
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)'
        },
        gap: { xs: '18px', sm: '22px', md: '26px' },
        mb: { xs: 2.5, md: 3.5 },
        width: '100%',
        mx: 'auto',
        justifyContent: 'center',
        alignItems: 'start',
        px: 0,
      }}
    >
      {features.map((feature, index) => (
        <Box
          key={index}
          sx={{
            width: '100%',
            height: 'fit-content',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Fade 
            in={loaded} 
            timeout={800} 
            style={{ 
              transitionDelay: `${index * 100}ms`,
              width: '100%',
              height: '100%',
            }}
          >
            <div style={{ width: '100%', height: '100%' }}>
              <FeatureCard
                feature={feature}
                index={index}
                onClick={handleFeatureClick}
              />
            </div>
          </Fade>
        </Box>
      ))}
    </Box>

    {/* Feature comparison note */}
    <Box 
      sx={{ 
        textAlign: 'center', 
        p: { xs: 2.5, md: 3.5 },
        mt: { xs: 1.25, md: 1.75 },
        borderRadius: designTokens.borderRadius.xl,
        background: `linear-gradient(135deg, ${designTokens.colors.lightBlue} 0%, ${designTokens.colors.white} 100%)`,
        border: `1px solid ${designTokens.colors.primary}20`,
        boxShadow: '0 4px 12px rgba(13, 28, 79, 0.08)',
        position: 'relative',
      }}
    >
      <Typography variant="h6" sx={{ 
        color: designTokens.colors.text, 
        mb: 2,
        fontWeight: 600,
        fontSize: { xs: '1.1rem', md: '1.2rem' }
      }}>
        🚀 All features included in every plan
      </Typography>
      <Typography variant="body1" sx={{ 
        color: designTokens.colors.textLight,
        maxWidth: 500,
        mx: 'auto',
        lineHeight: 1.6,
        fontSize: { xs: '1rem', md: '1.1rem' }
      }}>
        No hidden fees, no premium tiers. Every student gets access to the complete 
        PeerCampus ecosystem from day one.
      </Typography>
    </Box>
  </Container>
</Box>

        {/* Enhanced Stats Section */}
        {/* <Box 
  component="section" 
  aria-labelledby="stats-heading" 
  sx={{ 
    background: designTokens.colors.gradient, 
    py: { xs: 6, md: 7.5 }, 
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `
        radial-gradient(circle at 20% 80%, ${designTokens.colors.accent}15 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, ${designTokens.colors.warning}15 0%, transparent 50%)
      `,
      opacity: 0.6,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'url("data:image/svg+xml,%3Csvg width=\\"60\\" height=\\"60\\" viewBox=\\"0 0 60 60\\" xmlns=\\"http://www.w3.org/2000/svg\\"%3E%3Cg fill=\\"none\\" fill-rule=\\"evenodd\\"%3E%3Cg fill=\\"%23ffffff\\" fill-opacity=\\"0.05\\"%3E%3Ccircle cx=\\"30\\" cy=\\"30\\" r=\\"2\\"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
      opacity: 0.3,
    }
  }}
>
  <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
    <Typography 
      id="stats-heading" 
      component="h2" 
      sx={{ 
        display: 'none' // Hidden but accessible to screen readers
      }}
    >
      Platform Impact Statistics
    </Typography>

    <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 4.75 } }}>
      <Typography variant="h3" sx={{
        color: 'white',
        fontWeight: 700,
        fontSize: { xs: '1.8rem', md: '2.2rem' },
        mb: 2,
      }}>
        Making Real Impact Across Campuses
      </Typography>
      <Typography variant="body1" sx={{
        color: 'rgba(255,255,255,0.85)',
        fontSize: { xs: '1rem', md: '1.1rem' },
        maxWidth: 500,
        mx: 'auto',
      }}>
        Thousands of students are already transforming their campus experience
      </Typography>
    </Box>

    <Box sx={{ 
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: { xs: 3.5, md: 4.5 },
      maxWidth: 900,
      mx: 'auto',
    }}>
      {stats.map((stat, index) => (
        <Fade in={loaded} timeout={1000} style={{ transitionDelay: `${index * 200}ms` }} key={index}>
          <Box sx={{ 
            textAlign: 'center', 
            color: 'white', 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            gap: 2.25,
            minWidth: { xs: '200px', md: '220px' }, // FIXED: Consistent widths
            flex: { xs: '1 1 200px', md: '0 0 220px' }, // FIXED: Controlled flex behavior
          }}>
            <Box sx={{ 
              display: 'inline-flex', 
              p: { xs: 2.25, md: 2.5 }, 
              borderRadius: '50%', 
              background: `linear-gradient(135deg, ${stat.color} 0%, ${stat.color}dd 100%)`,
              boxShadow: `0 12px 30px ${stat.color}40`, 
              mb: 1,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: -2,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${stat.color}, transparent)`,
                opacity: 0.3,
                zIndex: -1,
              }
            }} 
            aria-hidden="true"
            >
              {React.cloneElement(stat.icon, { 
                sx: { 
                  fontSize: { xs: 34, md: 42 }, 
                  color: 'white',
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                }, 
                'aria-label': `${stat.label} icon` 
              })}
            </Box>

            <Typography 
              component="div" 
              sx={{ 
                fontWeight: 800, 
                fontSize: { xs: '2.75rem', md: '3.5rem' }, 
                background: `linear-gradient(135deg, #ffffff 0%, ${stat.color} 100%)`, 
                backgroundClip: 'text', 
                WebkitBackgroundClip: 'text', 
                WebkitTextFillColor: 'transparent', 
                lineHeight: 1,
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
              }}
            >
              <span 
                aria-live="polite" 
                aria-atomic="true" 
                aria-label={`${stat.number}${stat.suffix} ${stat.label}`}
              >
                {stat.number.toLocaleString()}
                {stat.suffix}
              </span>
            </Typography>

            <Typography 
              variant="h6" 
              sx={{ 
                color: 'rgba(255,255,255,0.95)', 
                fontWeight: 600, 
                fontSize: { xs: '1.05rem', md: '1.15rem' },
                letterSpacing: '0.5px',
              }}
            >
              {stat.label}
            </Typography>
          </Box>
        </Fade>
      ))}
    </Box>

    <Box sx={{ 
      textAlign: 'center', 
      mt: { xs: 4, md: 4.75 }, 
      pt: { xs: 3, md: 3.5 }, 
      borderTop: '1px solid rgba(255,255,255,0.2)',
    }}>
      <Typography variant="body2" sx={{ 
        color: 'rgba(255,255,255,0.8)', 
        fontSize: '0.9rem',
        fontWeight: 500,
      }}>
        🎓 Verified across 20+ premier universities • Last updated Aug 2025
      </Typography>
    </Box>
  </Container>
</Box> */}

      {/* Enhanced Testimonials Section */}
      {/* Enhanced Testimonials Section - REPLACE THE EXISTING TESTIMONIALS SECTION */}
<Container maxWidth="lg" sx={{ py: { xs: 3.5, md: 4.5 } }}>
  <Box sx={{ textAlign: 'center', mb: { xs: 4.5, md: 5.5 } }}>
      <Chip
      label="Student Success Stories"
      sx={{
        backgroundColor: `${designTokens.colors.purple}15`,
        color: designTokens.colors.purple,
        fontWeight: 600,
        mb: 1,
        fontSize: '0.9rem',
        height: 32,
      }}
      icon={<FormatQuote sx={{ fontSize: '18px !important' }} />}
    />
    
    <Typography 
      variant="h2" 
      sx={{ 
        ...designTokens.typography.h2,
        color: designTokens.colors.text, 
        mb: 2 
      }}
    >
      What Students Say
    </Typography>
    
    <Typography
      variant="h6"
      sx={{
        color: designTokens.colors.textLight,
        maxWidth: 600,
        mx: 'auto',
        lineHeight: 1.6,
        fontSize: { xs: '1.1rem', md: '1.2rem' },
        fontWeight: 500,
      }}
    >
      Real experiences from students who've transformed their campus life with PeerCampus
    </Typography>
  </Box>

  {/* Testimonials Carousel Container with Extra Padding for Scale Effect */}
  <Box sx={{ 
    position: 'relative', 
    overflow: 'hidden', 
    mb: 3,
    // Reduced padding to make section more compact
    py: 1,
    px: { xs: 1, md: 1.5 },
  }}>
    {/* Main carousel container */}
    <Box
      sx={{
        display: 'flex',
        transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: `translateX(-${currentTestimonial * (100 / safeTotalPages)}%)`,
        width: `${safeTotalPages * 100}%`,
      }}
    >
      {testimonialPages.map((page, pageIndex) => {
        const cardsInPage = page.length;
        const normalizedActiveIndex = Math.min(activeCardIndex, Math.max(0, cardsInPage - 1));
        const pageIsVisible = pageIndex === currentTestimonial;
        // Keep consistent card widths across pages (prevents the last page from stretching a single card to full width)
        const columnsForPage = Math.max(1, cardsPerView);

        return (
          <Box
            key={`testimonial-page-${pageIndex}`}
            sx={{
              flex: `0 0 ${100 / safeTotalPages}%`,
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              px: { xs: 0.5, md: 1.5 },
              py: 2,
            }}
          >
            {page.map((testimonial, cardIndex) => (
              <Box
                key={`testimonial-${pageIndex}-${cardIndex}`}
                sx={{
                  flex: `0 0 ${100 / columnsForPage}%`,
                  maxWidth: `${100 / columnsForPage}%`,
                  px: { xs: 1, md: 2 },
                  py: 2,
                  display: 'flex',
                }}
              >
                <TestimonialCard
                  testimonial={testimonial}
                  isActive={pageIsVisible && cardIndex === normalizedActiveIndex}
                  isVisible={pageIsVisible}
                />
              </Box>
            ))}
          </Box>
        );
      })}
    </Box>
  </Box>

  {/* Navigation Controls */}
  <Box sx={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center',
    gap: 3,
    mb: 3,
  }}>
    <IconButton
      onClick={() => {
        handleTestimonialNav('prev');
      }}
  aria-label="Previous testimonial"
  disabled={totalTestimonialPages <= 1}
      sx={{
        backgroundColor: designTokens.colors.white,
        color: designTokens.colors.primary,
        width: 48,
        height: 48,
        border: `1px solid ${designTokens.colors.primary}20`,
        boxShadow: '0 6px 18px rgba(13, 28, 79, 0.10)',
        '&&:hover': {
          background: `${designTokens.colors.gradient} !important`,
          color: `${designTokens.colors.white} !important`,
          borderColor: 'transparent',
          transform: 'translateY(-2px)',
          boxShadow: `0 10px 26px ${designTokens.colors.primary}35`,
        },
        '&&.Mui-focusVisible': {
          background: `${designTokens.colors.gradient} !important`,
          color: `${designTokens.colors.white} !important`,
          borderColor: 'transparent',
          boxShadow: `0 10px 26px ${designTokens.colors.primary}35`,
        },
        '&:disabled': {
          backgroundColor: `${designTokens.colors.white}70`,
          color: `${designTokens.colors.primary}40`,
          borderColor: `${designTokens.colors.primary}10`,
          boxShadow: 'none',
        },
        transition: 'all 0.25s ease',
      }}
    >
      <ChevronLeft sx={{ fontSize: 28 }} />
    </IconButton>

    {/* Enhanced Dot indicators */}
    <Box sx={{ display: 'flex', gap: 1 }}>
      {Array.from({ length: safeTotalPages }, (_, pageIndex) => (
        <Box
          key={pageIndex}
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: pageIndex === currentTestimonial 
              ? designTokens.colors.success // Green for current page
              : `${designTokens.colors.primary}30`,
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            position: 'relative',
            '&:hover': {
              backgroundColor: designTokens.colors.success,
              transform: 'scale(1.2)',
            },
            // Add mini indicators for active card within current page
            '&::after': pageIndex === currentTestimonial ? {
              content: '""',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 4,
              height: 4,
              borderRadius: '50%',
              backgroundColor: 'white',
              opacity: 0.8,
            } : {}
          }}
          onClick={() => {
            currentTestimonialRef.current = pageIndex;
            activeCardIndexRef.current = 0;
            setCurrentTestimonial(pageIndex);
            setActiveCardIndex(0);
          }}
          role="button"
          aria-label={`Go to testimonial page ${pageIndex + 1}`}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              currentTestimonialRef.current = pageIndex;
              activeCardIndexRef.current = 0;
              setCurrentTestimonial(pageIndex);
              setActiveCardIndex(0);
            }
          }}
        />
      ))}
    </Box>

    <IconButton
      onClick={() => {
        handleTestimonialNav('next');
      }}
  aria-label="Next testimonial"
  disabled={totalTestimonialPages <= 1}
      sx={{
        backgroundColor: designTokens.colors.white,
        color: designTokens.colors.primary,
        width: 48,
        height: 48,
        border: `1px solid ${designTokens.colors.primary}20`,
        boxShadow: '0 6px 18px rgba(13, 28, 79, 0.10)',
        '&&:hover': {
          background: `${designTokens.colors.gradient} !important`,
          color: `${designTokens.colors.white} !important`,
          borderColor: 'transparent',
          transform: 'translateY(-2px)',
          boxShadow: `0 10px 26px ${designTokens.colors.primary}35`,
        },
        '&&.Mui-focusVisible': {
          background: `${designTokens.colors.gradient} !important`,
          color: `${designTokens.colors.white} !important`,
          borderColor: 'transparent',
          boxShadow: `0 10px 26px ${designTokens.colors.primary}35`,
        },
        '&:disabled': {
          backgroundColor: `${designTokens.colors.white}70`,
          color: `${designTokens.colors.primary}40`,
          borderColor: `${designTokens.colors.primary}10`,
          boxShadow: 'none',
        },
        transition: 'all 0.25s ease',
      }}
    >
      <ChevronRight sx={{ fontSize: 28 }} />
    </IconButton>
  </Box>

  {/* Social proof section */}
  <Box sx={{
    p: { xs: 2, md: 3 },
    borderRadius: designTokens.borderRadius.xl,
    background: `linear-gradient(135deg, ${designTokens.colors.lightBlue} 0%, ${designTokens.colors.white} 100%)`,
    border: `1px solid ${designTokens.colors.primary}15`,
    textAlign: 'center',
  }}>
    <Typography variant="h5" sx={{
      color: designTokens.colors.text,
      mb: 2.5,
      fontWeight: 700,
    }}>
      Join the Growing Community
    </Typography>
    <Typography variant="body1" sx={{
      color: designTokens.colors.textLight,
      mb: 3,
      maxWidth: 600,
      mx: 'auto',
      lineHeight: 1.6,
    }}>
      Thousands of students have already discovered the power of AI-enhanced campus life. 
      Don't get left behind in the traditional way of doing things.
    </Typography>
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
      {[...Array(5)].map((_, i) => (
        <Star key={i} sx={{ fontSize: 24, color: '#F59E0B' }} />
      ))}
    </Box>
    <Typography variant="body2" sx={{ color: designTokens.colors.textLight }}>
      Average 4.9/5 rating across all universities
    </Typography>
  </Box>
</Container>

      {/* Enhanced CTA Section */}
      <Box 
        component="section" 
        aria-labelledby="cta-heading" 
        sx={{ 
          background: `linear-gradient(135deg, ${designTokens.colors.lightBlue} 0%, ${designTokens.colors.white} 100%)`,
          py: { xs: 6.5, md: 8 },
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `url("data:image/svg+xml,%3Csvg width=\\"80\\" height=\\"80\\" viewBox=\\"0 0 80 80\\" xmlns=\\"http://www.w3.org/2000/svg\\"%3E%3Cg fill=\\"none\\" fill-rule=\\"evenodd\\"%3E%3Cg fill=\\"%23${designTokens.colors.primary.substring(1)}\\" fill-opacity=\\"0.03\\"%3E%3Cpath d=\\"M0 0h80v80H0z\\"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            opacity: 0.5,
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
            <Typography 
              id="cta-heading" 
              component="h2" 
              sx={{ 
                ...designTokens.typography.h2, 
                color: designTokens.colors.text, 
                mb: 3,
                position: 'relative',
              }}
            >
              Ready to Transform Your{' '}
              <Box component="span" sx={{
                background: designTokens.colors.gradientAccent,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                position: 'relative',
              }}>
                Campus Experience
              </Box>
              ?
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                ...designTokens.typography.body, 
                color: designTokens.colors.textLight, 
                mb: { xs: 4, md: 4.5 }, 
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                fontWeight: 500,
                lineHeight: 1.6,
              }}
            >
              Join thousands of students already using PeerCampus to connect, learn, and grow together. 
              Start building your AI-powered campus community today and experience the future of student life.
            </Typography>

            <Box sx={{ 
              display: 'flex', 
              gap: { xs: 3, sm: 4 }, 
              justifyContent: 'center', 
              flexDirection: 'row', 
              alignItems: 'center',
              mb: 4,
            }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<School />}
                href={DEMO_PARTNER_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  background: designTokens.colors.gradient,
                  color: '#FFFFFF',
                  borderWidth: 0,
                  borderRadius: 'var(--btn-radius)',
                  px: { xs: 6, sm: 8 },
                  py: 2.5,
                  textTransform: 'none',
                  fontSize: { xs: '1.1rem', md: '1.2rem' },
                  fontWeight: 700,
                  minWidth: { xs: 280, sm: 220 },
                  minHeight: 56,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(135deg, #0A163F 0%, #16337C 100%)',
                    transform: 'translateY(100%)',
                    transition: 'transform 0.3s ease',
                    zIndex: -1,
                  },
                  '&:hover': {
                    color: '#FFFFFF',
                    transform: 'translateY(-3px)',
                    boxShadow: `0 8px 30px ${designTokens.colors.primary}40`,
                    '&::before': {
                      transform: 'translateY(0)',
                    }
                  },
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                Partner With Us
              </Button>
            </Box>

            {/* Additional CTA benefits */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: { xs: 1.5, md: 3 },
              opacity: 0.8,
            }}>
              {[
                '✨ Flexible plans',
                '🚀 Setup in 5 minutes',
                '🔒 University verified',
                '💬 24/7 AI support'
              ].map((benefit, index) => (
                <Typography 
                  key={index}
                  variant="body2" 
                  sx={{ 
                    color: designTokens.colors.textLight,
                    fontWeight: 500,
                    fontSize: { xs: '0.9rem', md: '1rem' },
                  }}
                >
                  {benefit}
                </Typography>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Enhanced Video Demo Modal */}
      <PeerCampusDemoModal
        open={videoModalOpen}
        onClose={() => setVideoModalOpen(false)}
        designTokens={designTokens}
        features={demoFeatures}
        videoUrl={DEMO_VIDEO_URL}
      />

      <ScrollToTopButton
        palette={{
          background: designTokens.colors.gradientAccent,
          shadow: '0 12px 35px rgba(1, 111, 87, 0.35)',
          hoverShadow: '0 16px 42px rgba(1, 111, 87, 0.45)',
        }}
      />

      <Footer />
      
      {/* Global styles for enhanced animations */}
      <Box
        component="style"
        sx={{
          display: 'none'
        }}
      >
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-20px) rotate(180deg); }
          }
          
          @keyframes sparkle {
            0%, 100% { opacity: 1; transform: scale(1) rotate(0deg); }
            50% { opacity: 0.7; transform: scale(1.1) rotate(180deg); }
          }
          
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
          
          @keyframes slideUp {
            from { transform: translateY(30px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
          
          /* Improved focus styles for accessibility */
          *:focus {
            outline: 2px solid ${designTokens.colors.accent};
            outline-offset: 2px;
          }
          
          /* Custom scrollbar for webkit browsers */
          ::-webkit-scrollbar {
            width: 8px;
          }
          
          ::-webkit-scrollbar-track {
            background: ${designTokens.colors.light};
          }
          
          ::-webkit-scrollbar-thumb {
            background: ${designTokens.colors.primary};
            border-radius: 4px;
          }
          
          ::-webkit-scrollbar-thumb:hover {
            background: ${designTokens.colors.secondary};
          }
        `}
      </Box>
    </Box>
  );
};

export default PeerCampusLanding;
